import { useState, useEffect } from 'react';
import { getDb, auth } from '@/lib/firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import logger from '@/lib/logging';
import { AppSettings, isDiagnosticLoggingEnabled } from '@/components/feedback-button/lib/env-service';

/**
 * Emulator users often have a legacy role doc and no accessGrants document.
 * app_settings read then denies. The emulator reports that deny as an evaluation
 * error (get() on the missing grant) with code permission-denied. That is the
 * same outcome as a normal deny: fall back to defaults, do not treat it as an app error.
 */
export function isExpectedAppSettingsReadDenial(error: unknown): boolean {
  const record =
    typeof error === 'object' && error !== null
      ? (error as { code?: unknown; message?: unknown })
      : null;
  const code = record?.code != null ? String(record.code) : '';
  const message =
    error instanceof Error
      ? error.message
      : record?.message != null
        ? String(record.message)
        : String(error ?? '');
  return (
    code === 'permission-denied' ||
    code.includes('permission') ||
    /evaluation error/i.test(message) ||
    message.includes('permission-denied')
  );
}

/**
 * Hook to listen for Diagnostic Logging setting changes
 *
 * Returns the current enabled state of the Diagnostic Logging setting.
 * Updates in real-time as the Firestore document changes.
 */
export function useDiagnosticLogging() {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    if (!auth) {
      void isDiagnosticLoggingEnabled().then(setIsEnabled);
      return () => {};
    }

    let snapUnsub: (() => void) | null = null;

    const refreshFromFetch = () => {
      void isDiagnosticLoggingEnabled().then(setIsEnabled);
    };

    const attachSnapshotIfSignedIn = (uid: string | null | undefined) => {
      if (snapUnsub) {
        snapUnsub();
        snapUnsub = null;
      }
      if (!uid) {
        refreshFromFetch();
        return;
      }
      try {
        const db = getDb();
        const settingsDocRef = doc(db, 'app_settings', 'global');
        snapUnsub = onSnapshot(
          settingsDocRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.data() as AppSettings;
              setIsEnabled(!!(data.diagnosticLoggingEnabled ?? data.authDebugLoggingEnabled));
            } else {
              setIsEnabled(false);
            }
          },
          (error) => {
            if (isExpectedAppSettingsReadDenial(error)) {
              logger.debug('useDiagnosticLogging: app_settings read denied, using fetched defaults');
              refreshFromFetch();
              return;
            }
            logger.error('Error in useDiagnosticLogging snapshot listener:', error);
            refreshFromFetch();
          }
        );
      } catch (error) {
        logger.error('Error setting up useDiagnosticLogging listener:', error);
        refreshFromFetch();
      }
    };

    refreshFromFetch();
    attachSnapshotIfSignedIn(auth.currentUser?.uid);

    const authUnsub = onAuthStateChanged(auth, (user) => {
      attachSnapshotIfSignedIn(user?.uid);
    });

    return () => {
      authUnsub();
      if (snapUnsub) snapUnsub();
    };
  }, []);

  return { isEnabled };
}
